import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, AppState, TelegramWebApp } from '../types';
import { authService } from '../services/authService';

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

interface AppContextType {
  state: AppState;
  login: () => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_AUTHENTICATED'; payload: boolean };

const initialState: AppState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload,
        isAuthenticated: action.payload !== null,
        isLoading: false,
        error: null
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload };
    default:
      return state;
  }
}

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const login = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      // Get Telegram WebApp data
      const tg = window.Telegram?.WebApp;
      if (!tg) {
        throw new Error('Telegram WebApp not available');
      }

      // Authenticate with backend using Telegram data
      const authResult = await authService.login(tg.initData);
      dispatch({ type: 'SET_USER', payload: authResult.user });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Login failed' });
    }
  };

  const logout = (): void => {
    authService.logout();
    dispatch({ type: 'SET_USER', payload: null });
  };

  const updateUser = (user: User): void => {
    dispatch({ type: 'SET_USER', payload: user });
  };

  const setError = (error: string | null): void => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  const setLoading = (loading: boolean): void => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  // Initialize app and attempt auto-login (Telegram or token-based)
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // First, check if we have an existing valid token (for both Telegram and password users)
        const existingToken = authService.getToken();
        if (existingToken && authService.isAuthenticated()) {
          try {
            // Validate the token with the server and get user data
            const validationResult = await authService.validateToken();
            dispatch({ type: 'SET_USER', payload: validationResult.user });
            console.log('✅ Auto-login successful with existing token');
            return;
          } catch (error) {
            console.log('🔄 Existing token invalid, clearing and continuing with fresh auth');
            authService.clearToken();
          }
        }

        // If no valid token, check for Telegram WebApp environment
        const tg = window.Telegram?.WebApp;
        if (tg) {
          // Configure Telegram WebApp
          if (typeof (tg as any).ready === 'function') (tg as any).ready();
          if (typeof (tg as any).expand === 'function') (tg as any).expand();
          
          // Apply theme
          document.documentElement.style.setProperty('--tg-theme-bg-color', tg.themeParams.bg_color || '#ffffff');
          document.documentElement.style.setProperty('--tg-theme-text-color', tg.themeParams.text_color || '#000000');
          document.documentElement.style.setProperty('--tg-theme-hint-color', tg.themeParams.hint_color || '#999999');
          document.documentElement.style.setProperty('--tg-theme-link-color', tg.themeParams.link_color || '#2481cc');
          document.documentElement.style.setProperty('--tg-theme-button-color', tg.themeParams.button_color || '#2481cc');
          document.documentElement.style.setProperty('--tg-theme-button-text-color', tg.themeParams.button_text_color || '#ffffff');

          // Try to auto-login with Telegram if we have valid init data
          if (tg.initData) {
            await login();
            return;
          }
        }

        // No valid token and no Telegram data - user needs to authenticate
        console.log('📱 No existing authentication, user needs to sign in');
        dispatch({ type: 'SET_LOADING', payload: false });
      } catch (error) {
        console.error('Failed to initialize app:', error);
        dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to initialize app' });
      }
    };

    initializeApp();
  }, []);

  const contextValue: AppContextType = {
    state,
    login,
    logout,
    updateUser,
    setError,
    setLoading,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextType {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}